'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { AetnaLogo } from '@/components/Icons/Insurance/AetnaLogo'
import { BlueCrossBlueShieldLogo } from '@/components/Icons/Insurance/BlueCrossBlueShieldLogo'
import { CignaLogo } from '@/components/Icons/Insurance/CignaLogo'
import { HumanaLogo } from '@/components/Icons/Insurance/GridView/HumanaLogo'
import { KaiserPermananteLogo } from '@/components/Icons/Insurance/KaiserPermanenteLogo'
import { UnitedHealthcareLogo } from '@/components/Icons/Insurance/UnitedHealthcareLogo'
import { MedicareLogo } from '@/components/Icons/Insurance/MedicareLogo'
import { MedicaidLogo } from '@/components/Icons/Insurance/GridView/MedicaidLogo'
import { textFadeInVariantsNoTransition } from '@/lib/animations'
import classes from './InsuranceAccepted.module.css'

type Crop = {
    canvasWidth: number
    canvasHeight: number
    contentWidth: number
    contentHeight: number
    offsetX: number
    offsetY: number
}

type LogoEntry = {
    name: string
    Logo: React.ComponentType
    /** Target display height (Figma glyph height, or optical match when the asset differs). */
    height: number
    opacity: number
    /** GridView SVGs need a crop window; width is derived from content aspect × height. */
    crop?: Crop
}

/**
 * Heights follow Figma where our SVG aspect matches.
 * Kaiser uses a taller optical height — our asset is icon+wordmark, not Figma’s flat mark.
 */
const INSURANCE_LOGOS: LogoEntry[] = [
    { name: 'Aetna', Logo: AetnaLogo, height: 14.032, opacity: 0.8 },
    { name: 'Blue Cross Blue Shield', Logo: BlueCrossBlueShieldLogo, height: 14.016, opacity: 1 },
    { name: 'Cigna', Logo: CignaLogo, height: 33.719, opacity: 0.9 },
    {
        name: 'Humana',
        Logo: HumanaLogo,
        height: 13.996,
        opacity: 1,
        crop: { canvasWidth: 163, canvasHeight: 50, contentWidth: 79, contentHeight: 17, offsetX: 42, offsetY: 16 },
    },
    { name: 'Kaiser Permanente', Logo: KaiserPermananteLogo, height: 20, opacity: 1 },
    { name: 'UnitedHealthcare', Logo: UnitedHealthcareLogo, height: 22.368, opacity: 1 },
    { name: 'Medicare', Logo: MedicareLogo, height: 12, opacity: 1 },
    {
        name: 'Medicaid',
        Logo: MedicaidLogo,
        height: 12,
        opacity: 1,
        crop: {
            canvasWidth: 163,
            canvasHeight: 50,
            contentWidth: 87.5,
            contentHeight: 14.8,
            offsetX: 38.3,
            offsetY: 18,
        },
    },
]

function LogoGlyph({ Logo, height, crop }: { Logo: React.ComponentType; height: number; crop?: Crop }) {
    if (crop == null) {
        return (
            <span className={classes.logoGlyph} style={{ height }}>
                <Logo />
            </span>
        )
    }

    const scale = height / crop.contentHeight
    const width = (height * crop.contentWidth) / crop.contentHeight

    return (
        <span className={classes.logoCrop} style={{ width, height }}>
            <span
                className={classes.logoCropInner}
                style={
                    {
                        width: crop.canvasWidth,
                        height: crop.canvasHeight,
                        '--logo-crop-x': `${-crop.offsetX * scale}px`,
                        '--logo-crop-y': `${-crop.offsetY * scale}px`,
                        '--logo-crop-scale-x': scale,
                        '--logo-crop-scale-y': scale,
                    } as React.CSSProperties
                }
            >
                <Logo />
            </span>
        </span>
    )
}

export default function InsuranceAccepted() {
    return (
        <motion.div
            className={classes.root}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px 0px' }}
            variants={textFadeInVariantsNoTransition}
        >
            <div className={classes.header}>
                <p className={classes.title}>Most major insurance accepted</p>
                <Link href="/insurance" className={classes.link} prefetch={false}>
                    See my copays
                </Link>
            </div>
            <div className={classes.logos} aria-label="Accepted insurance providers">
                {INSURANCE_LOGOS.map(({ name, Logo, height, opacity, crop }) => (
                    <div key={name} className={classes.logoItem} style={{ opacity }} aria-label={name}>
                        <LogoGlyph Logo={Logo} height={height} crop={crop} />
                    </div>
                ))}
                <span className={classes.more}>+ more</span>
            </div>
        </motion.div>
    )
}
